// Hook to fetch the /contract data

/*  Shared  */
import { Notify, Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  React  */
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useFetchTenantApplicationLinkAllContracts = ({
    clientId,
    includeUsers,
    partner,
    size
}) => {
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    // calling refetchContracts will force a rerender on the hook
    const [shouldRefetchContracts, setShouldRefetchContracts] = useState({});

    const refetchContracts = () => setShouldRefetchContracts({});

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const params = {
            includeApplication: true,
            size: size || -1,
            type: '*'
        };

        if (includeUsers) {
            params.includeUsers = true;
        }

        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        if (partner) {
            params.tenantMultiple = true;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get('/contract', {
                    cancelToken: source.token,
                    params
                });
                setContracts(Response.getData(response));
            } catch (error) {
                debug.e('Error Getting Contracts', error);
            }
            setLoading(false);
        };

        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [clientId, includeUsers, partner, shouldRefetchContracts, size]);

    return {
        contracts,
        loading,
        // returning the refetchContracts function so we can call it as needed.
        refetchContracts
    };
};

export const useFetchTenantApplicationLinkContract = ({
    applicationId,
    clientId,
    defaultContract
}) => {
    const [contract, setContract] = useState(defaultContract);
    const [contractExists, setContractExists] = useState(false);
    const [loading, setLoading] = useState(true);
    // calling refetchContract will force a rerender on the hook
    const [shouldRefetchContract, setShouldRefetchContract] = useState({});

    const refetchContract = () => setShouldRefetchContract({});

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const params = { applicationId };
        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get('/contract', {
                    cancelToken: source.token,
                    params
                });
                if (Response.getId(response)) {
                    setContract({
                        enabled: true,
                        ...Response.getOne(response)
                    });
                    setContractExists(true);
                } else {
                    setContract({
                        ...defaultContract,
                        applicationId
                    });
                    setContractExists(false);
                }
            } catch (error) {
                debug.e('Error Getting Contract', error);
            }
            setLoading(false);
        };

        fetchData();
    }, [applicationId, clientId, defaultContract, shouldRefetchContract]);

    return {
        contract,
        contractExists,
        loading,
        // returning the refetchContract function so we can call it as needed.
        refetchContract
    };
};

export const usePutTenantApplicationLinkContract = ({
    clientId,
    contract,
    t
}) => {
    const contractDetails = useMemo(() => ({ ...contract }), [contract]);
    delete contractDetails.enabled;

    const [saving, setSaving] = useState(false);

    const putTenantApplicationLinkContract = useCallback(async () => {
        try {
            setSaving(true);
            const params = contractDetails;
            if (clientId) {
                params.platformTenantId = clientId;
            }
            await axios.put(`/contract/${contractDetails.id}`, params);

            setSaving(false);
        } catch (err) {
            Notify.error(t('ApplicationContractManagement.errorUpdate'));
            setSaving(false);
        }
    }, [clientId, contractDetails, t]);

    return { putTenantApplicationLinkContract, saving };
};

export const usePostTenantApplicationLinkContract = ({
    clientId,
    contract,
    t
}) => {
    const contractDetails = useMemo(() => ({ ...contract }), [contract]);
    delete contractDetails.enabled;

    const [saving, setSaving] = useState(false);

    const postTenantApplicationLinkContract = useCallback(async () => {
        try {
            setSaving(true);
            const params = contractDetails;
            if (clientId) {
                params.platformTenantId = clientId;
            }
            await axios.post(`/contract/`, params);
            setSaving(false);
        } catch (err) {
            Notify.error(t('ApplicationContractManagement.errorSave'));
            setSaving(false);
        }
    }, [clientId, contractDetails, t]);

    return { postTenantApplicationLinkContract, saving };
};

export const useDeleteTenantApplicationLinkContract = ({
    clientId,
    contractId,
    t
}) => {
    const [saving, setSaving] = useState(false);

    const deleteTenantApplicationLinkContract = useCallback(async () => {
        try {
            setSaving(true);
            const params = {};
            if (clientId) {
                params.platformTenantId = clientId;
            }
            await axios.delete(`/contract/${contractId}`, { params });
            setSaving(false);
        } catch (err) {
            Notify.error(t('ApplicationContractManagement.errorDelete'));
            setSaving(false);
        }
    }, [clientId, contractId, t]);

    return { deleteTenantApplicationLinkContract, saving };
};
